import React from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../../styles/theme';
import type { QuickAddChip } from './buildPickerSections';

/**
 * QuickAddChips — FE-10.1.6 picker quick-add row.
 *
 * Renders a horizontal scrolling chip row above the section list.
 * Tapping a chip adds every item in `chip.itemIds` to selection. Chips
 * are additive — already-selected items in the chip's set don't
 * deselect on second tap.
 *
 * Active state: when every item id in the chip is currently in
 * `selectedIds`, the chip is rendered as filled-primary so the user
 * sees that the action is "already done."
 */

interface QuickAddChipsProps {
  chips: QuickAddChip[];
  selectedIds: Set<number>;
  onApply: (chip: QuickAddChip) => void;
}

export const QuickAddChips: React.FC<QuickAddChipsProps> = ({
  chips,
  selectedIds,
  onApply,
}) => {
  if (chips.length === 0) return null;

  return (
    <View style={styles.wrap}>
      <Text style={styles.heading}>Quick add</Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.row}
      >
        {chips.map((chip) => {
          const isFullyApplied =
            chip.itemIds.length > 0 &&
            chip.itemIds.every((id) => selectedIds.has(id));
          return (
            <Pressable
              key={chip.key}
              onPress={() => onApply(chip)}
              accessibilityRole="button"
              accessibilityLabel={`${chip.label}, ${chip.count} items`}
              style={({ pressed }) => [
                styles.chip,
                isFullyApplied && styles.chipActive,
                pressed && { opacity: 0.85 },
              ]}
              testID={`quick-add-chip-${chip.key}`}
            >
              <MaterialIcons
                name={isFullyApplied ? 'check-circle' : 'add-circle-outline'}
                size={16}
                color={isFullyApplied ? '#FFFFFF' : fpColors.primary}
              />
              <Text
                style={[
                  styles.chipText,
                  isFullyApplied && styles.chipTextActive,
                ]}
                numberOfLines={1}
              >
                {chip.label} · {chip.count}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    paddingTop: 12,
    paddingBottom: 4,
  },
  heading: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    paddingHorizontal: 20,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  row: {
    paddingHorizontal: 16,
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: fpColors.primary,
    backgroundColor: 'transparent',
  },
  chipActive: {
    backgroundColor: fpColors.primary,
  },
  chipText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },
  chipTextActive: {
    color: '#FFFFFF',
  },
});

export default QuickAddChips;
